import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import UserLayout from "../../components/user/dashboard/UserLayout";
import { useUserRentals } from "../../services/user/UserRentalsService";
import {
  ShoppingBag,
  Wallet,
  Calendar,
  Star,
  ArrowRight,
  AlertCircle,
  RefreshCw,
  Download,
  FileText,
  Activity
} from "lucide-react";

export default function DashboardOverview() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { getDashboardStats } = useUserRentals();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setIsLoading(true);
    const result = await getDashboardStats();
    if (result.success) {
      setStats(result.data);
    }
    setIsLoading(false);
  };

  const kpis = [
    {
      label: "Active Rentals",
      value: stats?.active_rentals ?? 0,
      icon: ShoppingBag,
      color: "#62397f",
      sub: "Currently with you"
    },
    {
      label: "Total Spent",
      value: `₦${(stats?.total_spent ?? 0).toLocaleString()}`,
      icon: Wallet,
      color: "#22C55E",
      sub: "Lifetime rental value"
    },
    {
      label: "Upcoming Events",
      value: stats?.upcoming_events ?? 0,
      icon: Calendar,
      color: "#F59E0B",
      sub: "Scheduled bookings"
    },
    {
      label: "Loyalty Tier",
      value: stats?.loyalty_tier ?? "Standard",
      icon: Star,
      color: "#EC4899",
      sub: "Based on total spent"
    },
  ];

  const getActivityIcon = (action) => {
    switch (action) {
      case 'rental_requested':
        return <FileText size={16} className="text-blue-500" />;
      case 'status_changed':
        return <RefreshCw size={16} className="text-purple-500" />;
      case 'invoice_downloaded':
        return <Download size={16} className="text-emerald-500" />;
      default:
        return <Activity size={16} className="text-gray-500" />;
    }
  };

  const formatActivityDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <UserLayout>
      <div className="p-4 md:p-8 lg:p-10 max-w-[1400px] mx-auto space-y-8 animate-fade-in">
        {/* Welcome Section */}
        <div>
          <h1 className="text-3xl font-medium text-[#111111] dark:text-white font-serif">
            Dashboard Overview
          </h1>
          <p className="text-[#666666] dark:text-[#A0A0A0] mt-1">
            Track your rentals, bills, and recent activity.
          </p>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <KPISkeleton key={i} />)
          ) : (
            kpis.map((kpi, idx) => (
              <div
                key={idx}
                onMouseEnter={() => setHoveredCard(idx)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] transition-all duration-300 ${hoveredCard === idx ? "shadow-xl -translate-y-1 border-[#62397f]/50" : "shadow-sm"
                  }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#F5F3FF] dark:bg-[#2A2440]">
                    <kpi.icon size={24} style={{ color: kpi.color }} />
                  </div>
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-medium text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider text-[10px] font-bold">{kpi.label}</h3>
                  <p className="text-2xl font-medium text-[#111111] dark:text-white font-serif">{kpi.value}</p>
                  <p className="text-[10px] text-[#999999] dark:text-[#777777] font-medium">{kpi.sub}</p>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] flex flex-col overflow-hidden shadow-sm">
            <div className="p-6 border-b border-[#F0F0F0] dark:border-[#2A2A2A] flex items-center justify-between">
              <h2 className="text-lg font-medium text-[#111111] dark:text-white font-serif">Recent Activity</h2>
              <Link to="/dashboard/activity" className="text-sm text-[#62397f] dark:text-[#A78BFA] font-bold uppercase tracking-wider hover:underline flex items-center gap-1 transition-all">
                View all <ArrowRight size={14} />
              </Link>
            </div>
            <div className="flex-1 min-h-[300px]">
              {isLoading ? (
                <div className="p-12 text-center text-[#666666] dark:text-[#A0A0A0]">
                  <div className="animate-spin w-6 h-6 border-2 border-[#62397f] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-sm">Loading activity...</p>
                </div>
              ) : stats?.recent_activity?.length > 0 ? (
                stats.recent_activity.map((activity, idx) => (
                  <div key={idx} className="p-6 flex items-start gap-4 hover:bg-[#F9FAFB] dark:hover:bg-[#212121] transition-colors border-b last:border-0 border-[#F0F0F0] dark:border-[#2A2A2A] group">
                    <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center border border-[#E5E7EB] dark:border-[#2A2A2A]">
                      {getActivityIcon(activity.action)}
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm text-[#111111] dark:text-white leading-tight font-medium uppercase tracking-tight">
                        {activity.action.replace('_', ' ')}
                      </p>
                      <p className="text-xs text-[#666666] dark:text-[#A0A0A0] line-clamp-1">{activity.details}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold text-[#999999] dark:text-[#777777] uppercase">{formatActivityDate(activity.created_at)}</p>
                      {activity.entity_id && (
                        <Link to={`/dashboard/rentals/${activity.entity_id}`} className="text-[10px] text-[#62397f] hover:underline block mt-1 font-bold italic">
                          View
                        </Link>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-20 text-center space-y-4">
                  <Activity size={32} className="mx-auto text-[#999999]/30" />
                  <p className="text-sm text-[#666666] dark:text-[#A0A0A0]">No recent activity found.</p>
                </div>
              )}
            </div>
          </div>

          {/* Tips Section */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#62397f] to-[#7C3AED] rounded-2xl p-8 text-white shadow-xl flex flex-col relative overflow-hidden group">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:scale-110 transition-transform duration-500"></div>

              <div className="relative z-10 flex-1">
                <Star className="mb-6 opacity-80" size={32} />
                <h3 className="text-xl font-medium font-serif mb-2">Loyalty Program</h3>
                <p className="text-white/80 text-sm mb-6 leading-relaxed">
                  You are currently a <span className="font-bold text-white uppercase">{stats?.loyalty_tier || "Standard"}</span> tier member.
                  Rent more to unlock exclusive discounts and priority support!
                </p>

                <div className="bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#62397f]">
                      <Star size={20} fill="currentColor" />
                    </div>
                    <div>
                      <p className="text-sm font-bold">{stats?.loyalty_tier} Status</p>
                      <p className="text-xs text-white/70">Keep it up!</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
              <h3 className="font-bold text-[#111111] dark:text-white mb-4 flex items-center gap-2">
                <AlertCircle size={18} className="text-[#62397f]" />
                Did you know?
              </h3>
              <p className="text-xs text-[#666666] dark:text-[#A0A0A0] leading-relaxed">
                You can download your invoices at any time from the "My Rentals" page. Simply click on a rental to see details and the download button.
              </p>
              <Link to="/dashboard/rentals" className="text-xs text-[#62397f] font-bold mt-4 inline-flex items-center gap-1 hover:translate-x-1 transition-transform">
                Go to My Rentals <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        .dark .animate-shimmer {
           background: linear-gradient(90deg, transparent, rgba(255,255,255,0.03), transparent);
           background-size: 200% 100%;
        }
      `}</style>
    </UserLayout>
  );
}

function KPISkeleton() {
  return (
    <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm animate-pulse">
      <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 mb-4"></div>
      <div className="space-y-3">
        <div className="h-2 w-20 bg-gray-100 dark:bg-gray-800 rounded"></div>
        <div className="h-6 w-24 bg-gray-100 dark:bg-gray-800 rounded"></div>
        <div className="h-2 w-32 bg-gray-100 dark:bg-gray-800 rounded"></div>
      </div>
    </div>
  );
}
